// src/components/MessagesPage.js

import React, { useState, useMemo, useEffect, useCallback } from 'react';
import './MessagesPage.css';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import Spinner from './Spinner';
import { removeMarkdownFences } from '../utils/HelperFunctions';
import { useData } from '../context/DataContext';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { SOCIAL_MEDIA_PLATFORMS } from '../config/MenuSocialMediaPlatformGeneral.js';

const MessagesPage = () => {
  const {
    queryResults,
    isLoadingQueryData,
    activeinfluencerId,
    setActiveinfluencerId,
    generateMessagesSummary,
    fetchMessagesSummary,
    saveMessagesSummary,
    socialMediaConnections,
  } = useData();

  const { currentUser } = useAuth();
  const location = useLocation(); // Get location

  const [messagesSummary, setMessagesSummary] = useState('');
  const [emptyMessages, setEmptyMessages] = useState([]);
  const [localEditableSummary, setLocalEditableSummary] = useState('');
  const [showSummary, setShowSummary] = useState(false);
  const [lastSummaryUpdateDate, setLastSummaryUpdateDate] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [warningMessage, setWarningMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [spinnerMessage, setSpinnerMessage] = useState('');

  // --- 1. Deriving state from context ---

  // We create a "Merged" influencer object.
  // Priority 1: Global Context (if loaded and complete)
  // Priority 2: Location State (passed from Modal)
  const activeInfluencer = useMemo(() => {
    // Try to find in global store
    const fromContext = (queryResults || []).find(
      (item) => item.influencerId === activeinfluencerId,
    );

    // If found in context, return it.
    // BUT, if we have an override channel from navigation, inject it here to prevent UI flicker
    if (fromContext) {
      if (location.state?.activeChannelOverride) {
        return {
          ...fromContext,
          activeInfluencerChannel: location.state.activeChannelOverride,
        };
      }
      return fromContext;
    }

    // If NOT in context (e.g. first load, missing from list), construct a "Stub" from navigation state
    if (location.state?.influencerId === activeinfluencerId) {
      return {
        influencerId: activeinfluencerId,
        name: location.state.influencerName || `ID: ${activeinfluencerId}`,
        activeInfluencerChannel: location.state.activeChannelOverride || null,
        status: 'LOADING...', // Placeholder status while fetch happens
        country: 'Loading...',
        idiom: 'Loading...',
        // Add other safe defaults to prevent crashes
      };
    }

    return null;
  }, [queryResults, activeinfluencerId, location.state]);

  // 1) On influencer change, load publications, products, and AI summary
  useEffect(() => {
    if (!activeinfluencerId) return;

    const loadData = async () => {
      try {
        setLoading(true);
        setIsSaving(false);
        setErrorMessage('');
        setEmptyMessages([]);
        setWarningMessage('');
        setMessagesSummary('');
        setLocalEditableSummary('');
        setShowSummary(false);
        setLastSummaryUpdateDate('');

        // Fetch influencer-level summary
        setSpinnerMessage(
          'Please wait while retrieving an AI In-depth analysis of the relationship ...',
        );
        const summaryResponse = await fetchMessagesSummary(activeinfluencerId);
        if (summaryResponse && summaryResponse.error) {
          console.error('API Error fetching emails:', summaryResponse.message);
          setWarningMessage(
            summaryResponse.message ||
              'No AI relationship available for this influencer. Please generate a new AI summary.',
          );
          return null; // Indicate failure
        } else {
          console.log('AI summary response: ', summaryResponse);
          const summaryString = summaryResponse?.strategicSummary || '';
          const summaryDate = summaryResponse?.dateLastUpdate || '';
          console.log('AI summary String: ', summaryString);
          console.log('SummaryDate: ', summaryDate);
          const cleanAiSummary = removeMarkdownFences(summaryString);

          setMessagesSummary(cleanAiSummary);
          setLocalEditableSummary(cleanAiSummary);
          setLastSummaryUpdateDate(summaryDate);
          setShowSummary(!!cleanAiSummary);
          setIsEditing(false);
        }
      } catch (err) {
        console.error('Error fetching summary:', err);
        setErrorMessage(err.message || 'Failed to load data');

        // Ensure old summary is cleared if an error occurs
        setMessagesSummary('');
        setLocalEditableSummary('');
        setShowSummary(false);
        setLastSummaryUpdateDate('');
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [
    activeinfluencerId,
    fetchMessagesSummary,
    setMessagesSummary,
    setLocalEditableSummary,
    setShowSummary,
    setErrorMessage,
  ]);

  const activeChannel = useMemo(() => {
    if (!queryResults || !activeinfluencerId) return null;
    return queryResults.find((inf) => inf.influencerId === activeinfluencerId);
  }, [queryResults, activeinfluencerId]);

  // Return user accounts for a platform (connected)
  const getUserPlatformAccounts = useCallback(
    (platformKey) => {
      if (!currentUser) return [];

      const userId = currentUser.uid;
      const platformConnections =
        socialMediaConnections[userId]?.[platformKey] || {};

      // Instead of checking for a "connected" string, we now check for the
      // existence of a connection object AND that its 'expired' flag is false.
      return Object.keys(platformConnections).filter((accountIdentifier) => {
        const connectionDetails = platformConnections[accountIdentifier];
        // An account is considered active if its details exist and it's not expired.
        return connectionDetails && connectionDetails.expired === false;
      });
    },
    [currentUser, socialMediaConnections],
  );

  // Return influencer accounts for a platform (from socialContacts)
  const getInfluencerPlatformAccounts = useCallback(
    (platformKey) => {
      if (!activeChannel || !activeChannel.socialContacts) return [];
      const arr = activeChannel.socialContacts[platformKey];
      if (Array.isArray(arr)) {
        return arr;
      }
      return [];
    },
    [activeChannel],
  );

  // Each row in the UI is a pair: user account, influencer account
  // We produce all pairs (cross product).
  const renderPlatformRows = useCallback(
    (platformKey) => {
      const userAccounts = getUserPlatformAccounts(platformKey);
      console.log('User accounts: ', userAccounts);
      const infAccounts = getInfluencerPlatformAccounts(platformKey);
      console.log('Influencer accounts: ', infAccounts);

      if (userAccounts.length === 0 && infAccounts.length === 0) {
        return null;
      }
      /* <div className="messages-input-container"> */
      const rows = [];
      userAccounts.forEach((uAcct, i) => {
        infAccounts.forEach((infAcct, j) => {
          const rowKey = `${platformKey}-${i}-${j}`;
          rows.push(
            <div className="messages-input-row" key={rowKey}>
              <p> You: </p>
              <input
                type="text"
                value={uAcct}
                onChange={() => {}}
                placeholder={`Your ${platformKey} account`}
                readOnly
              />
              <p> Influencer: </p>
              <input
                type="text"
                value={infAcct}
                onChange={() => {}}
                placeholder={`Influencer's ${platformKey} account`}
                readOnly
              />
            </div>,
          );
        });
      });
      /*   </div> */
      // If one side is empty, no cross product results.
      // If you need to show influencer accounts even when user has none, or vice versa,
      // you can do a second pass. This design strictly uses cross product.

      if (rows.length === 0) {
        return null;
      }
      return (
        <div className="messages-platform-block" key={`${platformKey}-block`}>
          <h4>{platformKey.charAt(0).toUpperCase() + platformKey.slice(1)}</h4>
          {rows}
        </div>
      );
    },
    [getUserPlatformAccounts, getInfluencerPlatformAccounts],
  );

  // Build final objects to pass to generateMessagesSummary
  // For each platform, we gather arrays of user & influencer accounts.
  // The LLM can see them in the format you prefer.
  const buildUserHandles = useCallback(() => {
    const handles = {};
    SOCIAL_MEDIA_PLATFORMS.forEach(({ key }) => {
      handles[key] = getUserPlatformAccounts(key);
    });
    return handles;
  }, [getUserPlatformAccounts]);

  const buildInfluencerHandles = useCallback(() => {
    const handles = {};
    SOCIAL_MEDIA_PLATFORMS.forEach(({ key }) => {
      handles[key] = getInfluencerPlatformAccounts(key);
    });
    return handles;
  }, [getInfluencerPlatformAccounts]);

  const handleGenerateSummary = useCallback(async () => {
    try {
      if (!activeinfluencerId) {
        setErrorMessage('No influencer is selected.');
        return;
      }
      setLoading(true);
      setErrorMessage('');
      setWarningMessage('');
      setEmptyMessages([]); // Reset empty messages
      setMessagesSummary(''); // Reset summary

      // Build user and influencer handles
      const userHandlesObj = buildUserHandles();
      const influencerHandlesObj = buildInfluencerHandles();

      // Validate handles
      if (!userHandlesObj || !influencerHandlesObj) {
        throw new Error('Invalid user or influencer handles.');
      }

      setSpinnerMessage(
        'Please wait while generating a new in-depth analysis of the relationship...',
      );

      // Generate summary
      const summaryResponse = await generateMessagesSummary({
        influencerId: activeinfluencerId,
        userHandles: userHandlesObj,
        influencerHandles: influencerHandlesObj,
      });

      if (summaryResponse.summary) {
        // SUCCESS PATH: A summary was generated.
        const summaryText = await fetchMessagesSummary(activeinfluencerId);
        if (summaryText && !summaryText.error) {
          const summaryString = summaryText?.strategicSummary || '';
          const summaryDate = summaryText?.dateLastUpdate || '';
          const cleanAiSummary = removeMarkdownFences(summaryString);

          setMessagesSummary(cleanAiSummary);
          setLocalEditableSummary(cleanAiSummary);
          setLastSummaryUpdateDate(summaryDate);
          setShowSummary(!!cleanAiSummary);
          setIsEditing(false);
        } else {
          setErrorMessage(
            summaryText.message ||
              'Failed to retrieve the newly generated analysis.',
          );
        }
      } else {
        // LOCAL FAILURE PATH: Generation failed for a non-critical reason.
        console.error(
          '[handleGenerateSummary] Summary generation failed locally. Response:',
          summaryResponse,
        );

        // Set the main, local error message to be displayed on the page.
        if (summaryResponse.message) {
          setErrorMessage(summaryResponse.message);
        }

        // Set the informational "empty" messages, which often explain the failure.
        // This will be displayed in its own dedicated JSX block.
        if (summaryResponse.emptyMessages?.length > 0) {
          setEmptyMessages(summaryResponse.emptyMessages);
        }
      }
      // --- END OF THE FINAL, CORRECT FIX ---
    } catch (err) {
      // This catch handles unexpected client-side or network errors
      console.error(
        'A client-side error occurred during summary generation:',
        err,
      );
      setErrorMessage(err.message || 'A client-side error occurred.');
    } finally {
      setLoading(false);
    }
  }, [
    activeinfluencerId,
    buildUserHandles,
    buildInfluencerHandles,
    generateMessagesSummary,
    fetchMessagesSummary,
    setErrorMessage,
    setEmptyMessages, // Ensure this state exists
    setMessagesSummary,
    setLocalEditableSummary,
    setShowSummary,
    setIsEditing,
  ]);

  const handleEditSummary = useCallback(() => {
    setIsEditing(true);
  }, []);

  const handleSummaryChange = useCallback((e) => {
    setLocalEditableSummary(e.target.value);
  }, []);

  const handleSaveSummary = useCallback(async () => {
    try {
      if (!activeinfluencerId) {
        setErrorMessage('No influencer selected to save summary for.');
        return;
      }
      setIsSaving(true);
      setErrorMessage('');

      await saveMessagesSummary(activeinfluencerId, localEditableSummary);

      setMessagesSummary(localEditableSummary);
      setIsEditing(false);
    } catch (err) {
      setErrorMessage(err.message || 'Failed to save messages summary.');
    } finally {
      setIsSaving(false);
    }
  }, [
    activeinfluencerId,
    localEditableSummary,
    saveMessagesSummary,
    setErrorMessage,
  ]);

  const currentIndex = useMemo(() => {
    if (!queryResults || !activeinfluencerId) return -1;
    return queryResults.findIndex(
      (inf) => inf.influencerId === activeinfluencerId,
    );
  }, [queryResults, activeinfluencerId]);

  const handlePrevious = useCallback(() => {
    if (!queryResults || currentIndex <= 0) return;
    setActiveinfluencerId(queryResults[currentIndex - 1].influencerId);
  }, [queryResults, currentIndex, setActiveinfluencerId]);

  const handleNext = useCallback(() => {
    if (
      !queryResults ||
      currentIndex < 0 ||
      currentIndex >= queryResults.length - 1
    )
      return;
    setActiveinfluencerId(queryResults[currentIndex + 1].influencerId);
  }, [queryResults, currentIndex, setActiveinfluencerId]);

  if (isLoadingQueryData && !activeInfluencer) {
    return <Spinner message="Loading relationship details..." />;
  }

  // 2. If not loading, but still missing (e.g. fetch failed), show error/empty.
  if (!activeInfluencer && activeinfluencerId && !isLoadingQueryData) {
    return (
      <div className="messages-page">
        <p className="noData">
          Could not load data for influencer ID {activeinfluencerId}. Please try
          refreshing the page.
        </p>
      </div>
    );
  }

  if (!queryResults || !queryResults.length || !activeinfluencerId) {
    return (
      <p className="noData">
        No data available. Please run a query to select influencers to work
        with.
      </p>
    );
  }

  return (
    <div className="messages-page">
      {loading && <Spinner message={spinnerMessage} />}

      <div className="header-container">
        <div className="header-group">
          <img
            src={activeChannel?.thumbnail}
            alt={`${activeChannel?.name} Thumbnail`}
            className="thumbnail"
          />
        </div>
        <div className="header-group">
          <h2>{activeChannel?.name}</h2>
          <p>
            Status: <span>{activeChannel?.status}</span>
          </p>
        </div>
        <div className="header-group">
          <p>
            Country: <span>{activeChannel?.country || 'N/A'}</span>
          </p>
          <p>
            Idiom: <span>{activeChannel?.idiom || 'N/A'}</span>
          </p>
        </div>
        <div className="header-group">
          <p>
            Nickname: <span>{activeChannel?.nickname || 'N/A'}</span>
          </p>
          <p>
            Last Contact:{' '}
            <span>
              {new Date(activeChannel?.dateLastContact).toLocaleString([], {
                day: '2-digit',
                month: 'short',
                year: 'numeric',
              }) || 'N/A'}
            </span>
          </p>
        </div>

        <div className="navigation">
          <button onClick={handlePrevious} disabled={currentIndex <= 0}>
            Previous
          </button>
          <span>
            {currentIndex + 1}/{queryResults.length}
          </span>
          <button
            onClick={handleNext}
            disabled={currentIndex >= queryResults.length - 1}
          >
            Next
          </button>
        </div>
      </div>

      <h2 className="messages-page-title">
        Influencer Relationship AI Analysis
      </h2>
      {showSummary && lastSummaryUpdateDate && (
        <p>
          Last Assessed on:{' '}
          <span>
            {lastSummaryUpdateDate
              ? new Date(lastSummaryUpdateDate).toLocaleString([], {
                  day: '2-digit',
                  month: 'short',
                  year: 'numeric',
                })
              : 'N/A'}
          </span>
        </p>
      )}
      {errorMessage && <p className="messages-error">{errorMessage}</p>}
      {warningMessage && (
        <p className="messages-summary-section">
          No AI Relationship summary for this influencer. Generate one below.
        </p>
      )}
      {emptyMessages.length > 0 && (
        <div className="messages-empty-notices">
          {emptyMessages.map(({ platform, message }) => (
            <p key={platform} className="messages-error">
              {platform.toUpperCase()}: {message}
            </p>
          ))}
        </div>
      )}

      {showSummary && (
        <div className="messages-summary-section">
          <>
            {isEditing ? (
              <textarea
                value={localEditableSummary}
                onChange={handleSummaryChange}
                rows={10}
              />
            ) : (
              <div className="messages-summary-display">
                <ReactMarkdown
                  remarkPlugins={[remarkGfm]}
                  components={{
                    h1: ({ children, ...props }) => (
                      <h1
                        style={{
                          fontSize: '0.1em',
                          marginBottom: '0rem',
                          color: '#2c2c2c',
                        }}
                        {...props}
                      >
                        {children}
                      </h1>
                    ),
                    h2: ({ children, ...props }) => (
                      <h2
                        style={{
                          fontSize: '2em',
                          fontWeight: 600,
                          marginTop: '2rem',
                          marginBottom: '0.5rem',
                          color: '#e0e0e0',
                        }}
                        {...props}
                      >
                        {children}
                      </h2>
                    ),
                    h3: ({ children, ...props }) => (
                      <h3
                        style={{
                          fontSize: '1.5em',
                          fontWeight: 500,
                          marginTop: '1.5rem',
                          marginBottom: '0.3rem',
                          color: '#ddd',
                        }}
                        {...props}
                      >
                        {children}
                      </h3>
                    ),
                    h4: ({ children, ...props }) => (
                      <h4
                        style={{
                          fontSize: '1.25em',
                          fontWeight: 300,
                          marginTop: '1.25rem',
                          marginBottom: '0.2rem',
                          color: '#999999',
                        }}
                        {...props}
                      >
                        {children}
                      </h4>
                    ),
                    p: ({ children, ...props }) => (
                      <p
                        style={{
                          fontSize: '1em',
                          marginTop: '10px',
                          marginBottom: '10px',
                          lineHeight: '1.5',
                          color: '#c0c0c0',
                        }}
                        {...props}
                      >
                        {children}
                      </p>
                    ),
                    li: ({ children, ...props }) => (
                      <li
                        style={{
                          fontSize: '1em',
                          marginLeft: '10px',
                          marginBottom: '15px',
                        }}
                        {...props}
                      >
                        {children}
                      </li>
                    ),
                    ol: ({ children, ...props }) => (
                      <ol
                        style={{
                          listStyleType: 'decimal',
                          fontSize: '1em',
                          lineHeight: '1.5',
                          paddingLeft: '2rem',
                          marginBottom: '1.5rem',
                          color: '#aaaaaa' /* Slightly dimmed text for lists */,
                        }}
                        {...props}
                      >
                        {children}
                      </ol>
                    ),

                    ul: ({ children, ...props }) => (
                      <ul
                        style={{
                          listStyleType: 'disc',
                          fontSize: '1em',
                          lineHeight: '1.5',
                          paddingLeft: '1.5rem',
                          marginBottom: '1.5rem',
                          color: '#aaaaaa',
                        }}
                        {...props}
                      >
                        {children}
                      </ul>
                    ),
                  }}
                >
                  {messagesSummary}
                </ReactMarkdown>
              </div>
            )}

            {!isEditing ? (
              <button onClick={handleEditSummary}>Edit</button>
            ) : (
              <button onClick={handleSaveSummary} disabled={isSaving}>
                {isSaving ? 'Saving...' : 'Save'}
              </button>
            )}
          </>
        </div>
      )}

      <div className="messages-input-section">
        <div className="messages-input-container">
          {SOCIAL_MEDIA_PLATFORMS.map(({ key }) => renderPlatformRows(key))}
        </div>
      </div>

      <button
        className="messages-generate-button"
        onClick={handleGenerateSummary}
        disabled={loading}
      >
        {loading ? 'Generating...' : 'Generate AI Relationship Analysis'}
      </button>
    </div>
  );
};

export default MessagesPage;
